import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { PasswordInput } from "@/components/PasswordInput";

const AuthLayout = ({ title, subtitle, children }) => (
  <div className="min-h-screen flex items-center justify-center earth-texture px-4">
    <div className="w-full max-w-md heritage-card p-8 relative">
      <div className="mb-6">
        <div className="label-uppercase text-[#993D22] mb-2">Ekom 1 · Evondo</div>
        <h1 className="font-serif-heritage text-3xl text-[#261911] mb-1">{title}</h1>
        <p className="text-sm text-[#6E5B4F]">{subtitle}</p>
      </div>
      {children}
    </div>
  </div>
);

export const LoginPage = () => {
  const { login } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t("welcomeBack"));
      nav("/");
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <AuthLayout title={t("welcomeBack")} subtitle={t("signIn")}>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <Label>{t("email")}</Label>
          <Input type="email" value={email} onChange={e => setEmail(e.target.value)} required data-testid="login-email-input" />
        </div>
        <div>
          <Label>{t("password")}</Label>
          <PasswordInput value={password} onChange={e => setPassword(e.target.value)} required testid="login-password-input" />
          <Link to="/mot-de-passe-oublie" className="block text-right text-xs text-[#993D22] mt-1" data-testid="login-forgot-password-link">Mot de passe oublié ?</Link>
        </div>
        <Button type="submit" className="btn-terra w-full" disabled={loading} data-testid="login-submit-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          {t("signIn")}
        </Button>
        <p className="text-sm text-center text-[#6E5B4F]">
          {t("noAccount")} <a href="/register" className="text-[#993D22] font-semibold" data-testid="to-register-link">{t("toRegister")}</a>
        </p>
      </form>
    </AuthLayout>
  );
};

export const RegisterPage = () => {
  const { register } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(name, email, password);
      toast.success(t("joinVillage"));
      nav("/");
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Error");
    } finally { setLoading(false); }
  };

  return (
    <AuthLayout title={t("joinVillage")} subtitle={t("signUp")}>
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div>
          <Label>{t("yourName")}</Label>
          <Input value={name} onChange={e => setName(e.target.value)} required data-testid="register-name-input" />
        </div>
        <div>
          <Label>{t("email")}</Label>
          <Input type="email" value={email} onChange={e => setEmail(e.target.value)} required data-testid="register-email-input" />
        </div>
        <div>
          <Label>{t("password")}</Label>
          <PasswordInput value={password} onChange={e => setPassword(e.target.value)} required minLength={8} testid="register-password-input" />
        </div>
        <Button type="submit" className="btn-terra w-full" disabled={loading} data-testid="register-submit-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          {t("signUp")}
        </Button>
        <p className="text-sm text-center text-[#6E5B4F]">
          {t("hasAccount")} <a href="/login" className="text-[#993D22] font-semibold" data-testid="to-login-link">{t("toLogin")}</a>
        </p>
      </form>
    </AuthLayout>
  );
};
